'use client'

import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import { useSWRConfig } from 'swr'

export interface AuthUser {
  email: string
  userId: string
  firstName: string
  companyId: string
  stores: string[]
}

interface AuthContextType {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>
  logout: () => void
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const router = useRouter()
  const pathname = usePathname()
  const { mutate } = useSWRConfig()

  // Restore session from HttpOnly cookie on mount.
  // Skip for embed routes — they use their own auth via EmbedAuthProvider.
  useEffect(() => {
    if (
      pathname.startsWith('/embed') ||
      pathname.startsWith('/storefront') ||
      pathname.startsWith('/onboarding')
    ) {
      setIsLoading(false)
      return
    }
    ;(async () => {
      try {
        let res = await fetch('/api/auth/me')
        let data = await res.json()

        // /api/auth/me is in middleware's PUBLIC_PATH_PREFIXES, so middleware
        // doesn't refresh for it — we retry once here on 401.
        if (res.status === 401) {
          const refresh = await fetch('/api/auth/refresh', { method: 'POST' })
          if (refresh.ok) {
            res = await fetch('/api/auth/me')
            data = await res.json()
          }
        }

        if (data?.success) {
          setUser(data.data as AuthUser)
        }
      } catch (error) {
        console.error('Failed to restore session:', error)
      } finally {
        setIsLoading(false)
      }
    })()
  }, [pathname])

  // Redirect to login if not authenticated and not on login page.
  // (Signal, the /scan self-serve funnel, and /settings — the in-app entry point into the funnel —
  // are reachable without auth in dev only; local review affordance mirroring the middleware bypass.
  // Never in production.)
  const devOpen =
    process.env.NODE_ENV !== 'production' &&
    (pathname.startsWith('/signal') ||
      pathname.startsWith('/scan') ||
      pathname.startsWith('/settings'))
  useEffect(() => {
    if (
      !isLoading &&
      !user &&
      pathname !== '/login' &&
      !pathname.startsWith('/embed') &&
      !pathname.startsWith('/storefront') &&
      !pathname.startsWith('/onboarding') &&
      !devOpen
    ) {
      router.push('/login')
    }
  }, [isLoading, user, pathname, router, devOpen])

  const login = useCallback(
    async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
      try {
        const response = await fetch('/api/auth/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
        })
        const data = await response.json()

        if (!data.success) {
          return { success: false, error: data.error || 'Failed to authenticate' }
        }

        setUser(data.data as AuthUser)
        return { success: true }
      } catch (error) {
        console.error('Login error:', error)
        return { success: false, error: 'Network error. Please try again.' }
      }
    },
    []
  )

  const logout = useCallback(() => {
    fetch('/api/auth/logout', { method: 'POST' }).finally(() => {
      setUser(null)
      // Logout is a client-side route change (not a reload), so SWR's cache would
      // otherwise survive and briefly show the next user the previous user's data
      // (e.g. the Storefront entitlement shell). Drop every cached key.
      mutate(() => true, undefined, { revalidate: false })
      router.push('/login')
    })
  }, [router, mutate])

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Shadow provider for embeds — lives in this file so it shares the same
// AuthContext object as useAuth(), avoiding module-duplication issues with Turbopack.
interface ShadowAuthProviderProps {
  user: AuthUser | null
  children: ReactNode
}

const noop = async (_email: string, _password: string) => ({
  success: false as const,
  error: 'Embed mode' as const,
})

export function ShadowAuthProvider({ user, children }: ShadowAuthProviderProps) {
  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading: false,
        login: noop,
        logout: () => {},
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
